import * as React from 'react';
import useRestricted from '../hooks/useRestricted';
import { observer } from 'mobx-react-lite';
import { AppContext } from '../contexts/AppContext';
import { useNavigate, useParams } from 'react-router-dom';
import TempoPlanner from '../components/plans/TempoPlanner';
import { PlotContainer } from '../components/common/PlotContainer';
import { Map } from '../components/plans/Map';
import { getLatLngForDistance } from '../utils/UnitsUtils';
import { StyleControl } from '../components/plans/StyleControl';
import { useIntl } from 'react-intl';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Snackbar from '@mui/material/Snackbar';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import useTheme from '@mui/material/styles/useTheme';
import useMediaQuery from '@mui/material/useMediaQuery';
import styled from '@mui/material/styles/styled';
import { TerrainControl } from '../components/plans/TerrainControl';
import Typography from '@mui/material/Typography';
import dayjs from "dayjs";
import { WeatherControl } from '../components/plans/WeatherControl';
import posthog from 'posthog-js';
import ComparePlanActivityDialog from '../components/plans/ComparePlanActivityDialog';
import LoadingButton from '@mui/lab/LoadingButton';
import { ComparisonView } from '../components/comparisons/ComparisonView';
import { StravaActivity, StravaActivityStreams } from '../services/StravaService';
import { Comparison } from '../services/ComparisonsService';
import Link from '@mui/material/Link';
import IconButton from '@mui/material/IconButton';
import MapIcon from '@mui/icons-material/Map';
import CompareIcon from '@mui/icons-material/CompareArrows';
import Tooltip from '@mui/material/Tooltip';
import DeleteIcon from '@mui/icons-material/Delete';
import { Permission } from '../services/UserService';

// Define a proper interface for the comparison data
export interface ComparisonData {
  activity: StravaActivity;
  streams: StravaActivityStreams | null;
  comparison: Comparison;
}

const PlanPanel = styled(Box)(({ theme }) => ({
  borderRight: `solid 1px ${theme.palette.divider}`,
  width: 800,
  [theme.breakpoints.down('md')]: {
    width: "100%",
  }
}));

const Planner: React.FC = observer(() => {

  const permission = useRestricted();
  const intl = useIntl();

  const theme = useTheme();
  const isUpMD = useMediaQuery(theme.breakpoints.up('md'));

  const { userService, plansService, mapService, comparisonsService, stravaService, nutritionProductsService, ordersService, reviewService } = React.useContext(AppContext);

  const { userId, planId } = useParams();

  const navigate = useNavigate();

  const urlParams = new URLSearchParams(window.location.search);
  const stravaCode = urlParams.get('code');
  const stravaError = urlParams.get('error');

  const [openMobileAlert, setOpenMobileAlert] = React.useState<boolean>(!isUpMD);
  const [openShareAlert, setOpenShareAlert] = React.useState<boolean>(false);
  const [showCopiedAlert, setShowCopiedAlert] = React.useState<boolean>(false);
  const [openCompareDialog, setOpenCompareDialog] = React.useState<boolean>(!!stravaCode || !!stravaError);
  const [openReplaceComparisonDialog, setOpenReplaceComparisonDialog] = React.useState<boolean>(false);
  const [openDeleteComparisonDialog, setOpenDeleteComparisonDialog] = React.useState<boolean>(false);

  const [comparisonError, setComparisonError] = React.useState<boolean>(false);
  const [comparisonErrorMsg, setComparisonErrorMsg] = React.useState<string>("plans.errorLoadingComparison");

  const [currentSection, setCurrentSection] = React.useState<number>(0);
  const [isReviewer, setIsReviewer] = React.useState<boolean>(false);
  const [accessCheckDone, setAccessCheckDone] = React.useState<boolean>(false);
  const [ownerDisplayName, setOwnerDisplayName] = React.useState<string>('');

  const plan = plansService.plan;

  const [comparisonData, setComparisonData] = React.useState<ComparisonData | null>(null);
  const [isLoadingComparison, setIsLoadingComparison] = React.useState<boolean>(false);

  const [viewMode, setViewMode] = React.useState<'map' | 'comparison'>('map');

  React.useEffect(() => {
    if (comparisonData) {
      setViewMode('comparison');
    }
  }, [comparisonData]);

  React.useEffect(() => {
    if (!userService.user?.id) {
      return;
    }
    nutritionProductsService.loadNutritionProducts(userService.user?.id || "" );
  }, [nutritionProductsService, userService.user?.id]);

  // Access control: allow owner OR reviewer
  React.useEffect(() => {
    if (!userService.user?.id || !planId || !userId) {
      return;
    }
    const isOwner = userService.user.id === userId;
    if (isOwner) {
      setAccessCheckDone(true);
      return;
    }
    // Not the owner — check if they have an accepted reviewer invite
    reviewService.checkReviewerAccess(userId, planId, userService.user.id)
      .then(hasAccess => {
        if (!hasAccess) {
          if (!userService.isLoadingUser) {
            navigate("/plans");
          }
        } else {
          setIsReviewer(true);
          reviewService.getOwnerDisplayName(userId).then(name => setOwnerDisplayName(name || ''));
        }
        setAccessCheckDone(true);
      });
  }, [userService.user?.id, userId, planId, userService.isLoadingUser, navigate, reviewService]);

  React.useEffect(() => {
    if (!userService.user?.id || !planId || !userId || !accessCheckDone) {
      if (!userService.isLoadingUser && !accessCheckDone && userService.user && (!planId || !userId)) {
        navigate("/plans");
      }
      return;
    }

    if (plan && plan.id === planId) {
      return;
    }

    // Load plan and comparison in parallel
    const loadPlanAndComparison = async () => {
      try {
        // Start plan loading, comparison loading, and subscription loading in parallel
        const planPromise = plansService.loadPlan(userId, planId);
        const comparisonPromise = comparisonsService.getComparisonByPlanId(planId, userId);
        const subscriptionPromise = ordersService.loadSubscription(
          userService.user?.id || "",
          userService.user?.email || "",
          userService.user?.proExpDate
        );

        // Wait for all to complete
        const [loadedPlan, comparison] = await Promise.all([planPromise, comparisonPromise, subscriptionPromise]);

        if (!loadedPlan) {
          navigate("/plans");
          return;
        }

        // Start GPX parsing
        const gpxSuccess = await plansService.parseCurrentPlanGpxFile();
        if (!gpxSuccess) {
          navigate("/plans");
          return;
        }

        if (comparison) {
          if (!loadedPlan.comparisonId) {
            await plansService.setPlanData({
              ...loadedPlan,
              comparisonId: comparison.id
            });
          }

          posthog.capture('comparison_loaded', {
            comparison_id: comparison.id,
            plan_id: loadedPlan.id
          });
        }
        // If plan references a comparison but none was found
        else if (loadedPlan.comparisonId) {
          setComparisonErrorMsg("plans.comparisonNotFound");
          setComparisonError(true);

          // Remove comparisonId property using destructuring
          const { comparisonId, ...planWithoutComparison } = loadedPlan;
          await plansService.setPlanData(planWithoutComparison);
        }
      } catch (error) {
        console.error("Error loading plan or comparison:", error);

        // Check if error is related to comparison
        if (error instanceof Error &&
          error.message.toLowerCase().includes('comparison')) {
          // Show comparison error
          setComparisonErrorMsg("plans.errorLoadingComparison");
          setComparisonError(true);

          // Try to update the plan if it loaded successfully
          if (plansService.plan) {
            const { comparisonId, ...planWithoutComparison } = plansService.plan;
            await plansService.setPlanData(planWithoutComparison);
          }
        } else {
          // Likely a plan loading error
          navigate("/plans");
        }
      }
    };

    loadPlanAndComparison();
  }, [userService.isLoadingUser, userService.user, plansService, planId, userId, userService.user?.id, navigate, plan, comparisonsService, ordersService, accessCheckDone]);

  // Handle comparison submission
  const handleComparisonSubmit = async (activityId?: number) => {
    if (!plan || !activityId || !userService.user?.id) {
      setOpenCompareDialog(false);
      return;
    }

    try {
      // Fetch activity details and streams in parallel
      const [activity, streams] = await Promise.all([
        stravaService.getActivityDetail(activityId),
        stravaService.getActivityStreams(activityId)
      ]);

      if (!activity) {
        setComparisonErrorMsg("plans.activityFetchFailed");
        setComparisonError(true);
        setOpenCompareDialog(false);
        return;
      }

      // Save the comparison
      const comparison = await comparisonsService.saveComparison(
        plan.id,
        userService.user.id,
        activity,
        undefined,
        streams || undefined
      );

      if (!comparison) {
        setComparisonErrorMsg("plans.comparisonSaveFailed");
        setComparisonError(true);
        setOpenCompareDialog(false);
        return;
      }

      // Update the plan with the comparison ID if needed
      if (!plan.comparisonId || plan.comparisonId !== comparison.id) {
        await plansService.setPlanData({
          ...plan,
          comparisonId: comparison.id
        });
      }

      posthog.capture('comparison_created', {
        comparison_id: comparison.id,
        plan_id: plan.id,
        activity_id: activityId
      });

    } catch (error) {
      console.error("Error creating comparison:", error);
      setComparisonErrorMsg("plans.comparisonCreateFailed");
      setComparisonError(true);
    } finally {
      setOpenCompareDialog(false);
    }
  };

  const handleDeleteComparison = async () => {
    if (!plan?.comparisonId || !userService.user?.id) {
      return;
    }

    try {
      // Delete the comparison
      await comparisonsService.deleteComparison(plan.comparisonId, userService.user.id);

      // Update the plan to remove the comparison ID
      const { comparisonId, ...planWithoutComparison } = plan;
      await plansService.setPlanData(planWithoutComparison);

      // Clear the comparison data state
      setComparisonData(null);

      // Set view mode back to map since comparison is gone
      setViewMode('map');

      // Log the event
      posthog.capture('comparison_deleted', {
        comparison_id: plan.comparisonId,
        plan_id: plan.id
      });
    } catch (error) {
      console.error("Error deleting comparison:", error);
      setComparisonErrorMsg("plans.errorDeletingComparison");
      setComparisonError(true);
    } finally {
      // Close the dialog
      setOpenDeleteComparisonDialog(false);
    }
  };

  React.useEffect(() => {
    if (plansService.bbox && isUpMD && permission !== Permission.loading) {
      mapService.init(plansService.bbox, permission);
    }
  }, [plansService.bbox, mapService, isUpMD, permission]);

  const handleGraphHover = (distance: number | null) => {
    if (!distance) {
      return;
    }
    const [lng, lat] = getLatLngForDistance(distance, plansService.latlng, plansService.distancePoints)
    mapService.setPointPostion(lat, lng);
  }

  const getShareUrl = () => {
    if (!userService.user?.id || !plan?.id) {
      return "";
    }

    const controlPoints = [...plan.controlPoints];
    controlPoints.pop();
    controlPoints.shift();
    return encodeURI(`${window.location.origin}/plans/new?route=${userService.user?.id}/${userService.user?.id}-${planId}${controlPoints.map((cp) => `&control-point=${cp.name}:${cp.distance},${cp.food ? "1" : "0"},${cp.drinks ? "1" : "0"},${cp.wc ? "1" : "0"},${cp.crew ? "1" : "0"},${cp.dropbag ? "1" : "0"}`).join("")}&date=${dayjs(plan.date).format("YYYY-MM-DD HH:mm:ss")}&name=${plan.name}&sport=${plan.sport || "run"}`);
  }

  const copyToClipboard = (text: string) => {
    posthog.capture('plan_share_url_copied', { url: text });
    navigator.clipboard.writeText(text);
    setShowCopiedAlert(true);
  }

  const handleDuplicate = () => {
    posthog.capture('plan_duplicate_clicked', { planId: plan?.id });
    if (!plan) {
      return;
    }
    plansService.duplicatePlan(plan).then((plan) => {
      if (plan) {
        navigate(`/plans/${userService.user?.id}/${plan.id}`);
      }
    });
  }

  const handleCompare = () => {
    if (!plan) {
      return;
    }

    // If plan already has a comparison, show the replace confirmation dialog
    if (plan.comparisonId) {
      setOpenReplaceComparisonDialog(true);
    } else {
      navigate(`/compare/${userId}/${planId}`);
    }
  }

  // Add a function to handle replacing the comparison
  const [isReplaceLoading, setIsReplaceLoading] = React.useState<boolean>(false);

  const handleReplaceComparison = async () => {
    if (!plan || !plan.comparisonId || !userService.user?.id) {
      return;
    }

    setIsReplaceLoading(true);

    try {
      // Delete the existing comparison
      await comparisonsService.deleteComparison(plan.comparisonId, userService.user.id);

      // Update the plan to remove the comparison ID
      const { comparisonId, ...planWithoutComparison } = plan;
      await plansService.setPlanData(planWithoutComparison);

      // Log the event
      posthog.capture('comparison_deleted_for_replacement', {
        comparison_id: plan.comparisonId,
        plan_id: plan.id
      });

      // Close the replacement dialog and navigate to the compare page
      setOpenReplaceComparisonDialog(false);
      navigate(`/compare/${userId}/${planId}`);
    } catch (error) {
      console.error("Error replacing comparison:", error);
      setComparisonErrorMsg("plans.errorReplacingComparison");
      setComparisonError(true);
    } finally {
      setIsReplaceLoading(false);
    }
  };

  // This effect runs when plan.comparisonId changes
  React.useEffect(() => {
    const loadComparisonData = async () => {
      if (!plan?.comparisonId || !userService.user?.id) {
        setComparisonData(null);
        return;
      }

      setIsLoadingComparison(true);

      try {
        // First, get the comparison object
        const comparison = await comparisonsService.getComparison(plan.comparisonId, userService.user.id);

        if (!comparison) {
          setComparisonErrorMsg("plans.comparisonNotFound");
          setComparisonError(true);

          // Remove comparisonId property if comparison not found
          const { comparisonId, ...planWithoutComparison } = plan;
          await plansService.setPlanData(planWithoutComparison);

          setComparisonData(null);
          return;
        }

        // Parse the activity data
        const activity = comparisonsService.getActivityFromComparison(comparison);

        if (!activity) {
          setComparisonErrorMsg("plans.errorParsingActivityData");
          setComparisonError(true);
          setComparisonData(null);
          return;
        }

        // Get the streams data if available
        let streams = null;
        if (comparison.streamsStoragePath) {
          streams = await comparisonsService.getStreamsFromComparison(comparison, userService.user.id);
        }

        // Set the comparison data for rendering
        setComparisonData({
          activity,
          streams,
          comparison
        });

        posthog.capture('comparison_data_loaded', {
          comparison_id: comparison.id,
          plan_id: plan.id
        });

      } catch (error) {
        console.error("Error loading comparison data:", error);
        setComparisonErrorMsg("plans.errorLoadingComparisonData");
        setComparisonError(true);
        setComparisonData(null);
      } finally {
        setIsLoadingComparison(false);
      }
    };

    loadComparisonData();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally keyed on comparisonId only; adding `plan` would reload on every plan field change
  }, [plan?.comparisonId, userService.user?.id, comparisonsService, plansService]);

  const activityDate = comparisonData?.activity.start_date ? new Date(comparisonData.activity.start_date).toLocaleDateString() : new Date().toLocaleDateString();

  // Add a toggle function
  const toggleViewMode = () => {
    setViewMode(prevMode => prevMode === 'map' ? 'comparison' : 'map');

    // Track the toggle action
    posthog.capture('toggle_view_mode', {
      plan_id: plan?.id,
      new_mode: viewMode === 'map' ? 'comparison' : 'map'
    });
  };

  return (
    !!(plan && plansService.sectionsData.length > 0 && plansService.latlng.length > 0) ? (
      <Box display="flex" height={isUpMD ? "100vh" : "auto"} overflow="hidden">
        <PlanPanel>
          <TempoPlanner
            plan={plan}
            setPlanData={isReviewer ? () => {} : plansService.setPlanData}
            currentSection={currentSection}
            changeSection={setCurrentSection}
            sectionsData={plansService.sectionsData}
            startLatlng={plansService.latlng[0]}
            endLatlng={plansService.latlng[plansService.latlng.length - 1]}
            isReviewer={isReviewer}
            ownerName={ownerDisplayName}
            plot={
              <Box paddingX={1} zIndex={10} position="relative">
                <PlotContainer
                  id="section-plot"
                  data={plansService.elevationSeries.filter(e => e.distance <= plansService.sectionsData[currentSection].endDistance && e.distance >= plansService.sectionsData[currentSection].startDistance)}
                  dimensions={{ width: 500, height: 200, margin: { top: 20, right: 0, bottom: 30, left: 40 } }}
                  breaks={[]}
                  setHoveredDistance={handleGraphHover}
                  selectionBoxBottom={true}
                  surfaceData={plansService.plan.surfaceData?.filter(e => e.distance && e.distance <= plansService.sectionsData[currentSection].endDistance && e.distance >= plansService.sectionsData[currentSection].startDistance)}
                />
              </Box>
            }
            surfaceData={plansService.plan.surfaceData}
            onShare={() => {
              posthog.capture('plan_share_clicked', { planId: plan.id });
              setOpenShareAlert(true)
            }}
            onDuplicate={handleDuplicate}
            onCompare={handleCompare}
          />
        </PlanPanel>
        {isUpMD && (
          <Box display="flex" flexDirection="column" width="100%" height="100%">
            {/* Comparison header - always shown when comparison data is available */}
            {comparisonData && (
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                padding={1}
                bgcolor={theme.palette.background.paper}
                borderBottom={1}
                borderColor="divider"
                zIndex={10}
              >
                <Box flex={1}>
                  <Typography variant="subtitle1" fontWeight="bold">
                    {intl.formatMessage({ id: "plans.comparedTo" })}:{" "}
                    <Tooltip title={intl.formatMessage({ id: "plans.viewOnStrava" })}>
                      <Link
                        href={`https://www.strava.com/activities/${comparisonData.activity.id}`}
                        target="_blank"
                        rel="noopener"
                        underline="hover"
                        color="inherit"
                        sx={{ textDecoration: "underline" }}
                      >
                        {comparisonData.activity.name} • {activityDate || ""} • {comparisonData.activity.type}
                      </Link>
                    </Tooltip>
                  </Typography>
                </Box>
                <Box>
                  {!isReviewer && (
                    <Tooltip title={intl.formatMessage({ id: "plans.deleteComparison" })}>
                      <IconButton
                        size="small"
                        onClick={() => setOpenDeleteComparisonDialog(true)}
                        sx={{ mr: 1 }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                  <Tooltip title={intl.formatMessage({ id: viewMode === 'comparison' ? 'plans.showMap' : 'plans.showComparison' })}>
                    <IconButton
                      size="small"
                      onClick={toggleViewMode}
                    >
                      {viewMode === 'comparison' ? <MapIcon fontSize="small" /> : <CompareIcon fontSize="small" />}
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
            )}

            {/* Map section - shown if no comparison or if view mode is map */}

            <Box flexGrow={viewMode === 'map' ? 1 : 0} position="relative">
              <Map />

              {(!comparisonData || viewMode === 'map') && (
                <>
                  <StyleControl />
                  <TerrainControl />
                  <WeatherControl />
                </>
              )}
            </Box>

            {/* Comparison section - shown only when data is available and view mode is comparison */}
            {comparisonData && viewMode === 'comparison' && (
              <Box
                display="flex"
                flexDirection="column"
              >
                <ComparisonView
                  plan={plan}
                  comparisonData={comparisonData}
                  sectionsData={plansService.sectionsData}
                  isLoading={isLoadingComparison}
                />
              </Box>
            )}

            {/* Plot container always at the bottom with fixed height */}
            <Box padding={1} zIndex={10} minHeight="300px" borderTop={1} borderBottom={1} borderColor="divider">
              <PlotContainer
                id="full-plot"
                data={plansService.elevationSeries}
                dimensions={{ width: 500, height: 300, margin: { top: 20, right: 0, bottom: 30, left: 40 } }}
                breaks={plansService.plan.controlPoints ? plansService.plan.controlPoints.slice(1, plansService.plan.controlPoints.length - 1).map((v) => v.distance * 1000) : []}
                setHoveredDistance={handleGraphHover}
                surfaceData={plansService.plan.surfaceData}
              />
            </Box>
          </Box>
        )}

        <Snackbar
          open={plansService.isSavingPlan}
          message={intl.formatMessage({ id: "data.saving" })}
          ContentProps={{
            sx: {
              background: "#ffffff",
              color: "#000000"
            }
          }}
        />

        <Snackbar
          open={plansService.hasSaveError}
          message={intl.formatMessage({ id: "data.savingError" })}
          autoHideDuration={6000}
          ContentProps={{
            sx: {
              background: "#e51f1f"
            }
          }}
        />

        <Snackbar
          open={showCopiedAlert}
          message={intl.formatMessage({ id: "plans.copied" })}
          autoHideDuration={3000}
          color="success"
          onClose={() => setShowCopiedAlert(false)}
        />

        <Dialog
          open={openMobileAlert}
          onClose={() => setOpenMobileAlert(false)}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
          maxWidth="xs"
        >
          <DialogTitle id="alert-dialog-title">
            {intl.formatMessage({ id: "plans.mobileAlertTitle" })}
          </DialogTitle>
          <DialogContent>
            <DialogContentText id="alert-dialog-description">
              {intl.formatMessage({ id: "plans.mobileAlertText" })}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenMobileAlert(false)}>
              {intl.formatMessage({ id: "action.ok" })}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={openShareAlert}
          onClose={() => setOpenShareAlert(false)}
          aria-labelledby="share-alert-dialog-title"
          aria-describedby="share-alert-dialog-description"
          maxWidth="md"
        >
          <DialogTitle id="share-alert-dialog-title">
            {intl.formatMessage({ id: "plans.shareTitle" })}
          </DialogTitle>
          <DialogContent>
            <Box paddingX={1} zIndex={10}>
              <PlotContainer
                id="full-plot-dialog"
                data={plansService.elevationSeries}
                dimensions={{ width: 500, height: 300, margin: { top: 20, right: 0, bottom: 30, left: 40 } }}
                breaks={plansService.plan.controlPoints ? plansService.plan.controlPoints.slice(1, plansService.plan.controlPoints.length - 1).map((v) => v.distance * 1000) : []}
                setHoveredDistance={handleGraphHover}
              />
            </Box>
            <DialogContent>
              <DialogContentText>{intl.formatMessage({ id: "plans.shareText" })}</DialogContentText>
              <Box marginTop={1} padding={1} borderRadius={1} onClick={() => copyToClipboard(getShareUrl())} style={{ cursor: "pointer", backgroundColor: "#eeeeee" }}>
                <Typography component="p" variant="subtitle2" whiteSpace="nowrap" overflow="hidden" textOverflow="ellipsis">{getShareUrl()}</Typography>
                <Box display="flex" justifyContent="center" marginTop={1}>
                  <Typography variant="caption" textAlign="center">*{intl.formatMessage({ id: "plans.clickToCopy" })}</Typography>
                </Box>
              </Box>
            </DialogContent>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenShareAlert(false)}>
              {intl.formatMessage({ id: "action.close" })}
            </Button>
            <Button onClick={() => {
              copyToClipboard(getShareUrl());
              setOpenShareAlert(false);
            }}>
              {intl.formatMessage({ id: "action.closeAndCopy" })}
            </Button>
          </DialogActions>
        </Dialog>

        <Snackbar
          open={comparisonError}
          message={intl.formatMessage({ id: comparisonErrorMsg })}
          autoHideDuration={6000}
          onClose={() => setComparisonError(false)}
          ContentProps={{
            sx: {
              background: "#e51f1f"
            }
          }}
        />

        {/* Add the replacement confirmation dialog */}
        <Dialog
          open={openReplaceComparisonDialog}
          onClose={() => setOpenReplaceComparisonDialog(false)}
          aria-labelledby="replace-comparison-dialog-title"
        >
          <DialogTitle id="replace-comparison-dialog-title">
            {intl.formatMessage({ id: "plans.replaceComparisonTitle" })}
          </DialogTitle>
          <DialogContent>
            <DialogContentText>
              {intl.formatMessage({ id: "plans.replaceComparisonText" })}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenReplaceComparisonDialog(false)}>
              {intl.formatMessage({ id: "action.cancel" })}
            </Button>
            <LoadingButton
              onClick={handleReplaceComparison}
              loading={isReplaceLoading}
            >
              {intl.formatMessage({ id: "action.replace" })}
            </LoadingButton>
          </DialogActions>
        </Dialog>

        <Dialog
          open={openDeleteComparisonDialog}
          onClose={() => setOpenDeleteComparisonDialog(false)}
          aria-labelledby="delete-comparison-dialog-title"
        >
          <DialogTitle id="delete-comparison-dialog-title">
            {intl.formatMessage({ id: "plans.deleteComparisonTitle" })}
          </DialogTitle>
          <DialogContent>
            <DialogContentText>
              {intl.formatMessage({ id: "plans.deleteComparisonText" })}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenDeleteComparisonDialog(false)}>
              {intl.formatMessage({ id: "action.cancel" })}
            </Button>
            <LoadingButton
              onClick={handleDeleteComparison}
            >
              {intl.formatMessage({ id: "action.delete" })}
            </LoadingButton>
          </DialogActions>
        </Dialog>

        <ComparePlanActivityDialog
          open={openCompareDialog}
          onClose={() => setOpenCompareDialog(false)}
          onSubmit={handleComparisonSubmit}
          stravaCode={stravaCode}
          stravaError={stravaError}
          date={plan.date}
        />
      </Box>
    ) : (<Box height="100vh" width="100%" display="flex" alignItems="center" justifyContent="center"><CircularProgress /></Box>)
  );
});

export default Planner;
